'use client';

import React from 'react';
import Link from 'next/link';
import { buttonVariants } from '@/components/ui/Button';
import {
  Rocket,
  TrendingUp,
  Building,
  Globe,
  ArrowRight,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';

const industries = [
  'SaaS', 'FinTech', 'HealthTech', 'AI / ML', 'CleanTech',
  'E-Commerce', 'EdTech', 'DeepTech', 'Marketplace', 'Cybersecurity',
  'IoT', 'Biotech', 'PropTech', 'InsurTech', 'FoodTech',
  'Mobility', 'SpaceTech', 'AgriTech', 'Web3', 'Gaming',
];

export default function SolutionsContent() {
  const { t } = useTranslation();
  const { ref: heroRef, isVisible: heroVisible } = useIntersectionObserver();
  const { ref: stagesRef, isVisible: stagesVisible } = useIntersectionObserver();
  const { ref: industriesRef, isVisible: industriesVisible } = useIntersectionObserver();
  const { ref: ctaRef, isVisible: ctaVisible } = useIntersectionObserver();

  const stages = [
    {
      icon: <Zap className="h-8 w-8 text-white" />,
      color: 'from-blue-500 to-blue-600',
      borderColor: 'border-blue-100',
      bgColor: 'bg-blue-50',
      title: t('pageContent.solutions.stages.preSeed.title'),
      subtitle: t('pageContent.solutions.stages.preSeed.subtitle'),
      description: t('pageContent.solutions.stages.preSeed.description'),
      plan: 'Starter',
    },
    {
      icon: <Rocket className="h-8 w-8 text-white" />,
      color: 'from-primary-500 to-primary-600',
      borderColor: 'border-primary-100',
      bgColor: 'bg-primary-50',
      title: t('pageContent.solutions.stages.seed.title'),
      subtitle: t('pageContent.solutions.stages.seed.subtitle'),
      description: t('pageContent.solutions.stages.seed.description'),
      plan: 'Professional',
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-white" />,
      color: 'from-violet-500 to-violet-600',
      borderColor: 'border-violet-100',
      bgColor: 'bg-violet-50',
      title: t('pageContent.solutions.stages.seriesA.title'),
      subtitle: t('pageContent.solutions.stages.seriesA.subtitle'),
      description: t('pageContent.solutions.stages.seriesA.description'),
      plan: 'Scale',
    },
    {
      icon: <Building className="h-8 w-8 text-white" />,
      color: 'from-emerald-500 to-emerald-600',
      borderColor: 'border-emerald-100',
      bgColor: 'bg-emerald-50',
      title: t('pageContent.solutions.stages.growth.title'),
      subtitle: t('pageContent.solutions.stages.growth.subtitle'),
      description: t('pageContent.solutions.stages.growth.description'),
      plan: 'Scale',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container">
          <div ref={heroRef} className={`max-w-3xl mx-auto text-center fade-in-section ${heroVisible ? 'is-visible' : ''}`}>
            <span className="inline-block bg-primary-50 text-primary-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {t('pageContent.solutions.hero.tagline')}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('pageContent.solutions.hero.title')}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {t('pageContent.solutions.hero.subtitle')}
            </p>
            <Link href="/pricing" className={buttonVariants({ size: 'lg', className: 'px-8 hover-scale' })}>
              {t('pageContent.solutions.cta.button')} <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Stages Section */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div ref={stagesRef} className={`text-center mb-16 fade-in-section ${stagesVisible ? 'is-visible' : ''}`}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('pageContent.solutions.stages.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {stages.map((stage, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl shadow-sm border ${stage.borderColor} p-8 hover:shadow-md transition-shadow hover-lift fade-in-section ${stagesVisible ? 'is-visible' : ''}`}
                style={{ transitionDelay: `${index * 0.15}s` }}
              >
                <div className="flex items-start gap-5">
                  <div className={`w-14 h-14 bg-gradient-to-br ${stage.color} rounded-xl flex items-center justify-center flex-shrink-0 shadow-md`}>
                    {stage.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="text-xl font-bold text-gray-900">{stage.title}</h3>
                      <span className={`${stage.bgColor} text-gray-700 px-3 py-0.5 rounded-full text-xs font-semibold`}>
                        {stage.plan}
                      </span>
                    </div>
                    <p className="text-sm text-primary-600 font-medium mb-3">{stage.subtitle}</p>
                    <p className="text-gray-600">{stage.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container">
          <div ref={industriesRef} className={`text-center mb-12 fade-in-section ${industriesVisible ? 'is-visible' : ''}`}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('pageContent.solutions.industries.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('pageContent.solutions.industries.subtitle')}
            </p>
          </div>

          <div className={`flex flex-wrap justify-center gap-3 max-w-4xl mx-auto fade-in-section ${industriesVisible ? 'is-visible' : ''}`} style={{ transitionDelay: '0.2s' }}>
            {industries.map((industry, index) => (
              <span
                key={index}
                className="bg-white border border-gray-200 text-gray-700 px-5 py-2.5 rounded-full text-sm font-medium hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-default"
              >
                {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="container text-center">
          <div ref={ctaRef} className={`fade-in-section ${ctaVisible ? 'is-visible' : ''}`}>
            <Globe className="h-12 w-12 mx-auto mb-6 text-primary-200" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('pageContent.solutions.cta.title')}
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              {t('pageContent.solutions.cta.subtitle')}
            </p>
            <Link
              href="/pricing"
              className={buttonVariants({ variant: 'secondary', size: 'lg', className: 'px-8 hover-scale' })}
            >
              {t('pageContent.solutions.cta.button')}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
